import { useEffect, useState } from 'react'
import { ErrorBanner, useAsync } from '../../components/Async'
import { ConfirmDialog } from '../../components/ConfirmDialog'
import { ApiError, seatLimit } from '../../lib/api'

/**
 * How many devices this organization may enroll.
 *
 * A commercial number rather than a security one — it is the seats a customer bought — so it sits
 * under Devices, where the fleet is, rather than under Settings. On the Enrollment tab specifically,
 * beside the kit that carries the number: a kit allows as many machines as the organization has seats,
 * and the card explaining the figure and the card spending it were on two different tabs (D-429).
 *
 * It shows what is in use against what is allowed because the limit alone is not actionable: the
 * question an operator actually has is "can I still roll this out", and that needs both halves. The
 * suggested numbers are one click each and the field takes anything, because a ceiling chosen here
 * would be discovered by somebody who had already paid for more than it allows.
 *
 * Lowering it below what is already enrolled is permitted and says so. Nothing is retired and no
 * device stops working; the organization simply enrolls no more until it is under the line. A console
 * that silently disconnected machines because a subscription changed would be making a decision
 * nobody asked it to make.
 *
 * Only a reduction asks first, and the asymmetry is the point rather than an oversight. Raising the
 * limit breaks nothing and refuses nobody, so a question there is friction with no failure behind it.
 * Lowering it is the one thing on this card that can stop a rollout — every enrollment past the line
 * is refused, on a machine somebody is standing at, with the seat limit named in the refusal and no
 * hint that a person changed it a moment ago.
 */
export function SeatLimitCard() {
  const seats = useAsync(() => seatLimit.get(), [])
  const [draft, setDraft] = useState('')
  const [saving, setSaving] = useState(false)
  const [failure, setFailure] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)
  const [asking, setAsking] = useState<number | null>(null)

  const current = seats.value

  // The field follows the server until somebody types, so a reload does not leave a stale number in
  // a box that looks authoritative.
  useEffect(() => {
    if (current) setDraft(String(current.seatLimit))
  }, [current?.seatLimit])

  /** Asks when the number goes down, and applies it directly when it does not. */
  function choose(value: number) {
    if (current && value < current.seatLimit) setAsking(value)
    else void save(value)
  }

  async function save(value: number) {
    setFailure(null)
    setSaved(false)
    setSaving(true)
    try {
      await seatLimit.set(value)
      await seats.reload()
      setSaved(true)
    } catch (error) {
      setFailure(error instanceof ApiError ? error.message : 'The seat limit could not be changed.')
    } finally {
      setSaving(false)
    }
  }

  const typed = Number(draft)
  const valid = Number.isInteger(typed) && typed >= 1 && (!current || typed <= current.ceiling)
  const over = current ? current.inUse > current.seatLimit : false

  return (
    <div className="card">
      <div className="card-body">
        <h3 style={{ marginTop: 0 }}>Device seats</h3>
        <p className="muted" style={{ marginTop: 0 }}>
          How many devices this organization may enroll. A deployment kit carries this number, so
          changing it here changes what the next kit allows.
        </p>

        <ErrorBanner message={failure ?? seats.error} />

        {current && (
          <>
            {/*
              A meter, because the question is "can I still roll this out" and that is a proportion.

              The figures stay in words above it - four numerals run together are the kind of thing
              somebody misreads by a factor of ten while sizing a subscription - and the bar is what
              answers the question at a glance without being read at all.
            */}
            <p data-testid="seat-usage" style={{ fontSize: '1.1rem', margin: '0 0 0.4rem' }}>
              <strong>{current.inUse.toLocaleString()}</strong> of{' '}
              <strong>{current.seatLimit.toLocaleString()}</strong> seats in use
              {!over && <span className="muted"> — {current.remaining.toLocaleString()} left</span>}
            </p>

            <span className="meter">
              <span
                className={over ? 'meter-fill over' : 'meter-fill'}
                style={{ width: `${Math.min(100, Math.round((current.inUse / Math.max(current.seatLimit, 1)) * 100))}%` }}
              />
            </span>

            {over && (
              <p className="hint" data-testid="seat-limit-over">
                This organization has more devices enrolled than it has seats. Nothing has been
                retired and no device has stopped working — no further devices can enroll until the
                limit is raised or machines are retired.
              </p>
            )}

            {/*
              The editor waits behind a summary, and the meter does not.

              Changing a seat limit is done once when a subscription changes and never again, while the
              figure above it is read every time somebody opens this tab to build a kit. Seven choice
              cards and a number field standing open for that is the same fault the forms behind buttons
              fixed elsewhere (D-421), and on this tab it was a third of the column (D-436).
            */}
            <details className="subsection" data-testid="seat-limit-editor">
              <summary>Change the limit</summary>

              <div className="theme-choices" role="group" aria-label="Suggested seat limits">
                {current.suggested.map((value) => (
                  <label
                    key={value}
                    className={`theme-choice${current.seatLimit === value ? ' selected' : ''}`}
                    data-testid={`seat-limit-${value}`}
                  >
                    <input
                      type="radio"
                      name="seat-limit"
                      value={value}
                      disabled={saving}
                      checked={current.seatLimit === value}
                      onChange={() => choose(value)}
                    />
                    <span className="theme-name">{value.toLocaleString()}</span>
                  </label>
                ))}
              </div>

              <div className="field-row" style={{ marginTop: '0.75rem', display: 'flex', gap: '0.5rem' }}>
                <input
                  type="number"
                  min={1}
                  max={current.ceiling}
                  value={draft}
                  disabled={saving}
                  aria-label="Seat limit"
                  data-testid="seat-limit-input"
                  onChange={(event) => setDraft(event.target.value)}
                />
                <button
                  type="button"
                  className="secondary"
                  disabled={saving || !valid || typed === current.seatLimit}
                  data-testid="seat-limit-save"
                  onClick={() => choose(typed)}
                >
                  Set
                </button>
              </div>

              <p className="hint" data-testid="seat-limit-explanation">
                The agent installs per user, so each Windows user who installs it is a separate device
                and uses a seat: two people sharing a PC are two seats. Retiring a device gives its seat
                back. Reimaging a machine and enrolling it again does not use a second one.
                {!current.configured && ' Nobody has chosen a number yet, so this is the default.'}
                {saved && ' Saved.'}
              </p>
            </details>
          </>
        )}
      </div>

      <ConfirmDialog
        open={asking !== null}
        title="Lower the seat limit"
        confirmLabel={asking === null ? 'Lower it' : `Lower to ${asking.toLocaleString()}`}
        danger={Boolean(current && asking !== null && asking < current.inUse)}
        busy={saving}
        onConfirm={() => {
          if (asking === null) return
          const value = asking
          setAsking(null)
          void save(value)
        }}
        onClose={() => setAsking(null)}
        data-testid="seat-limit-confirm"
      >
        {current && asking !== null && asking < current.inUse ? (
          <div className="banner warning" role="note" data-testid="seat-limit-confirm-over">
            {current.inUse.toLocaleString()} devices are already enrolled, which is more than{' '}
            {asking.toLocaleString()}. Nothing is retired and no device stops working, but no further
            machine can enroll until the limit is raised or devices are retired — including a machine
            being reimaged onto a seat it is not yet counted against.
          </div>
        ) : null}

        <p>
          The next deployment kit carries this number, and an enrollment past it is refused on the
          machine rather than here.
        </p>
      </ConfirmDialog>
    </div>
  )
}
